import type { LevelCatalog, LevelCatalogEntry } from "@gridflow/levels";
import { getLevelById, getLevelByOrder } from "@gridflow/levels";

export const GRIDFLOW_PROGRESS_STORAGE_KEY = "gridflow.progress.v1";

export interface ProgressStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface GridFlowProgressSnapshot {
  readonly currentLevelId: string;
  readonly highestUnlockedOrder: number;
  readonly ghostGuideSeenLevelIds: readonly string[];
}

export interface ProgressService {
  load(): GridFlowProgressSnapshot;
  setCurrentLevel(levelId: string): GridFlowProgressSnapshot;
  markLevelComplete(levelId: string): GridFlowProgressSnapshot;
  hasSeenGhostGuide(levelId: string): boolean;
  markGhostGuideSeen(levelId: string): GridFlowProgressSnapshot;
  reset(): GridFlowProgressSnapshot;
}

interface SerializedProgress {
  readonly currentLevelId?: unknown;
  readonly highestUnlockedOrder?: unknown;
  readonly ghostGuideSeenLevelIds?: unknown;
}

export class LocalProgressService implements ProgressService {
  public constructor(
    private readonly catalog: LevelCatalog,
    private readonly storage: ProgressStorage | null,
    private readonly key = GRIDFLOW_PROGRESS_STORAGE_KEY
  ) {}

  public load(): GridFlowProgressSnapshot {
    return this.read();
  }

  public setCurrentLevel(levelId: string): GridFlowProgressSnapshot {
    const entry = this.requireEntry(levelId);
    const previous = this.read();
    const next = this.normalize({
      ...previous,
      currentLevelId: entry.id,
      highestUnlockedOrder: Math.max(previous.highestUnlockedOrder, entry.order)
    });
    this.write(next);
    return next;
  }

  public markLevelComplete(levelId: string): GridFlowProgressSnapshot {
    const completed = this.requireEntry(levelId);
    const previous = this.read();
    const nextEntry =
      getLevelByOrder(this.catalog, completed.order + 1) ??
      getLevelByOrder(this.catalog, completed.order);

    const next = this.normalize({
      ...previous,
      currentLevelId: nextEntry?.id ?? completed.id,
      highestUnlockedOrder: Math.max(previous.highestUnlockedOrder, completed.order + 1)
    });
    this.write(next);
    return next;
  }

  public hasSeenGhostGuide(levelId: string): boolean {
    return this.read().ghostGuideSeenLevelIds.includes(levelId);
  }

  public markGhostGuideSeen(levelId: string): GridFlowProgressSnapshot {
    this.requireEntry(levelId);
    const previous = this.read();
    const seen = new Set(previous.ghostGuideSeenLevelIds);
    seen.add(levelId);
    const next = this.normalize({
      ...previous,
      ghostGuideSeenLevelIds: [...seen]
    });
    this.write(next);
    return next;
  }

  public reset(): GridFlowProgressSnapshot {
    const next = this.defaultSnapshot();
    try {
      this.storage?.removeItem(this.key);
    } catch {
      return next;
    }
    return next;
  }

  private read(): GridFlowProgressSnapshot {
    if (!this.storage) {
      return this.defaultSnapshot();
    }

    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) {
        return this.defaultSnapshot();
      }
      return this.normalize(JSON.parse(raw) as SerializedProgress);
    } catch {
      return this.defaultSnapshot();
    }
  }

  private write(snapshot: GridFlowProgressSnapshot): void {
    try {
      this.storage?.setItem(this.key, JSON.stringify(snapshot));
    } catch {
      return;
    }
  }

  private normalize(input: SerializedProgress): GridFlowProgressSnapshot {
    const firstEntry = this.requireFirstEntry();
    const currentLevel =
      typeof input.currentLevelId === "string"
        ? getLevelById(this.catalog, input.currentLevelId)
        : null;
    const currentLevelId = currentLevel?.id ?? firstEntry.id;
    const maxOrder = this.catalog.entries.length;
    const highestUnlockedOrder =
      typeof input.highestUnlockedOrder === "number" && Number.isFinite(input.highestUnlockedOrder)
        ? clampOrder(Math.floor(input.highestUnlockedOrder), firstEntry.order, maxOrder)
        : firstEntry.order;
    const ghostGuideSeenLevelIds = Array.isArray(input.ghostGuideSeenLevelIds)
      ? input.ghostGuideSeenLevelIds.filter(
          (levelId): levelId is string =>
            typeof levelId === "string" && getLevelById(this.catalog, levelId) !== null
        )
      : [];

    return {
      currentLevelId,
      highestUnlockedOrder: Math.max(highestUnlockedOrder, currentLevel?.order ?? firstEntry.order),
      ghostGuideSeenLevelIds: [...new Set(ghostGuideSeenLevelIds)]
    };
  }

  private defaultSnapshot(): GridFlowProgressSnapshot {
    const firstEntry = this.requireFirstEntry();
    return {
      currentLevelId: firstEntry.id,
      highestUnlockedOrder: firstEntry.order,
      ghostGuideSeenLevelIds: []
    };
  }

  private requireEntry(levelId: string): LevelCatalogEntry {
    const entry = getLevelById(this.catalog, levelId);
    if (!entry) {
      throw new Error(`Unknown GridFlow level "${levelId}".`);
    }
    return entry;
  }

  private requireFirstEntry(): LevelCatalogEntry {
    const firstEntry = getLevelByOrder(this.catalog, 1) ?? this.catalog.entries[0];
    if (!firstEntry) {
      throw new Error("GridFlow catalog must contain at least one level.");
    }
    return firstEntry;
  }
}

export function createBrowserProgressService(catalog: LevelCatalog): ProgressService {
  return new LocalProgressService(catalog, safeLocalStorage());
}

export function createMemoryProgressStorage(
  seed: Record<string, string> = {}
): ProgressStorage & { readonly snapshot: Record<string, string> } {
  const values = { ...seed };
  return {
    snapshot: values,
    getItem(key: string) {
      return Object.prototype.hasOwnProperty.call(values, key) ? (values[key] ?? null) : null;
    },
    setItem(key: string, value: string) {
      values[key] = value;
    },
    removeItem(key: string) {
      Reflect.deleteProperty(values, key);
    }
  };
}

function safeLocalStorage(): ProgressStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function clampOrder(order: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, order));
}
